import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMenu } from "../contexts/MenuContext";
import apiService from "../services/apiService";

const PromotionalPopupDisplay = ({ onItemClick }) => {
  const { selectedLanguage, auth } = useAuth();
  const { handleMenuClick, handleCategoryClick, items } = useMenu();
  const location = useLocation();
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const [open, setOpen] = useState(false);
  const hasAttemptedFetch = useRef(false);

  useEffect(() => {
    const fetchPromotionalPopup = async () => {
      try {
        // Only show popup if:
        // 1. Auth is loaded
        // 2. User is a regular user (not admin)
        // 3. User came from the startup page (has fromStartup state)
        // 4. Haven't already attempted to fetch for this mount
        if (!auth || !auth.role) return;
        if (auth.role !== "user") return;
        if (!location.state?.fromStartup) return;
        if (hasAttemptedFetch.current) return;

        hasAttemptedFetch.current = true;

        // Clear the state immediately so it doesn't persist on refresh
        window.history.replaceState({}, document.title);

        const response = await apiService.get("/promotional-popups");
        const popupData = response?.data?.data;

        if (popupData) {
          setPopup(popupData);
          setOpen(true);
        }
      } catch (error) {
        console.error("Error fetching promotional popup:", error);
      }
    };

    // Use setTimeout to ensure this doesn't block the menu page loading
    const timer = setTimeout(() => {
      fetchPromotionalPopup();
    }, 500);

    return () => clearTimeout(timer);
  }, [auth, location.state]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleActionClick = async () => {
    try {
      if (popup.menu_id && handleMenuClick) {
        setOpen(false);
        await handleMenuClick(popup.menu_id);

        if (popup.category_id && handleCategoryClick) {
          handleCategoryClick(popup.category_id);

          if (popup.item_id && onItemClick) {
            try {
              const itemResponse = await apiService.get(`/items/menu/${popup.menu_id}`);
              const menuItems = itemResponse?.data?.data || [];
              const item = menuItems.find((i) => Number(i.id) === Number(popup.item_id));

              if (item) {
                setTimeout(() => {
                  onItemClick(item);
                }, 300);
              }
            } catch (error) {
              console.error("Error fetching item:", error);
            }
          }
        }
        else if (popup.item_id && onItemClick) {
          try {
            const itemResponse = await apiService.get(`/items/menu/${popup.menu_id}`);
            const menuItems = itemResponse?.data?.data || [];
            const item = menuItems.find((i) => Number(i.id) === Number(popup.item_id));

            if (item && item.categories && item.categories.length > 0 && handleCategoryClick) {
              const firstCategory = item.categories[0];
              handleCategoryClick(Number(firstCategory.id));
              setTimeout(() => {
                onItemClick(item);
              }, 300);
            }
          } catch (error) {
            console.error("Error fetching item:", error);
          }
        }
      } else {
        handleClose();
      }
    } catch (error) {
      console.error("Error in promotional popup action:", error);
      handleClose();
    }
  };

  if (!popup) return null;

  // Get the title in the selected language or fallback to default
  const getLocalizedTitle = () => {
    if (!selectedLanguage || selectedLanguage.language_id === 0) {
      return popup.title;
    }

    const languageData = popup.languages?.find(
      (lang) => Number(lang.language_id) === Number(selectedLanguage.language_id)
    );

    return languageData?.title || popup.title;
  };

  const title = getLocalizedTitle();

  const hasViewAction = popup.menu_id;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      {popup.image && (
        <Box
          component="img"
          src={popup.image}
          alt={title}
          sx={{
            width: "100%",
            minHeight: { xs: "200px", sm: "250px" },
            maxHeight: { xs: "300px", sm: "400px" },
            objectFit: "cover",
            backgroundColor: "grey.100",
          }}
        />
      )}

      <DialogContent sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          {title}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, justifyContent: hasViewAction ? "space-between" : "center" }}>
        {hasViewAction ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="primary"
              sx={{
                py: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                minWidth: { xs: "120px", sm: "200px" },
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleActionClick}
              variant="contained"
              color="primary"
              sx={{
                py: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                minWidth: { xs: "120px", sm: "200px" },
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              View
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            variant="contained"
            color="primary"
            sx={{
              py: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              minWidth: { xs: "150px", sm: "200px" },
              fontSize: { xs: "14px", sm: "16px" },
            }}
          >
            Got it!
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PromotionalPopupDisplay;
